import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { IAgentPosition, IAgentPositionState, IThunkAPI } from '@/store/types';

import { agentPositionApi } from '@/api';
import {
  isActionFromSlice,
  isFulfilledAction,
  isKnownError,
  isPendingAction,
  isRejectedAction,
  makeCancellable,
  onFulfilledAction,
  onPendingAction,
  onRejectedAction,
} from '@/helpers';
import { SLICES } from '@/constants/actions';

const initialState: IAgentPositionState = {
  agentPositions: {},
  meta: {
    pending: [],
  },
};

export const getAgentPositionListByClientId = createAsyncThunk<IAgentPosition[], number, IThunkAPI>(
  'agentPosition/list/client',
  async (clientId, thunkAPI) => {
    try {
      const getAgentPositionListByClientId = makeCancellable(
        agentPositionApi.getAgentPositionListByClientId,
        thunkAPI.signal,
      );
      const { data } = await getAgentPositionListByClientId({ clientId });
      if (isKnownError(data)) throw data;

      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getAgentPositionByClientIdAndCountryIds = createAsyncThunk<
  IAgentPosition[],
  { clientId: number; countryIds: number[] },
  IThunkAPI
>('agentPosition/client/countryIds', async ({ clientId, countryIds }, thunkAPI) => {
  try {
    const getAgentPositionByClientIdAndCountryIds = makeCancellable(
      agentPositionApi.getAgentPositionListByClientIdAndCountryIds,
      thunkAPI.signal,
    );
    const { data } = await getAgentPositionByClientIdAndCountryIds({ clientId, countryIds });
    if (isKnownError(data)) throw data;

    return data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

const sortByName = (a: IAgentPosition, b: IAgentPosition) => {
  if (a.agentPositionName < b.agentPositionName) {
    return -1;
  }
  if (a.agentPositionName > b.agentPositionName) {
    return 1;
  }
  return 0;
};

export const agentPositionSlice = createSlice({
  name: 'agentPosition',
  initialState,
  reducers: {
    clearAgentPosition(state) {
      state.agentPositions = {};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAgentPositionListByClientId.fulfilled, (state, action) => {
        state.agentPositions[action.meta.arg] = action.payload?.sort(sortByName) || [];
      })
      .addCase(getAgentPositionByClientIdAndCountryIds.fulfilled, (state, action) => {
        state.agentPositions[action.meta.arg.clientId] = action.payload || [];
      })
      .addMatcher(
        action => isFulfilledAction(action) && isActionFromSlice(action, SLICES.AGENT_POSITION),
        onFulfilledAction,
      )
      .addMatcher(
        action => isRejectedAction(action) && isActionFromSlice(action, SLICES.AGENT_POSITION),
        onRejectedAction,
      )
      .addMatcher(
        action => isPendingAction(action) && isActionFromSlice(action, SLICES.AGENT_POSITION),
        onPendingAction,
      );
  },
});

// actions
export const { clearAgentPosition } = agentPositionSlice.actions;

export default agentPositionSlice.reducer;
